import { Title } from '@solidjs/meta'
import { useParams, useSearchParams } from '@solidjs/router'
import { type Component, createEffect, createResource, createSignal, onCleanup, Show } from 'solid-js'
import Button from '~/components/Button'
import Card from '~/components/Card'
import Container from '~/components/Container'
import Form from '~/components/Form'
import GoBack from '~/components/GoBack'
import Tabs from '~/components/Tabs'
import { useToast } from '~/components/useToast'
import { useForm } from '~/hooks/useForm'
import type { SettingsFormType } from '~/layers/ui/admin/empresas'
import { settingsSchema, TabAuditoria, TabCatalogos, TabCotizaciones, TabCrm, TabFormatos, TabGeneral, TabMarca, TabPagos, TabPasarelas } from '~/layers/ui/admin/empresas'
import { companyService } from '~/services/company.service'
import { setTrailOverride } from '~/stores/breadcrumb'
import type { CompanySettingsType } from '~/types/Settings'
import type { TabItemType } from '~/types/Tabs'
import { resolveError } from '~/utils/error'
import { DEFAULT_SETTINGS, mergeSettings } from '~/utils/settings'

/** Pestañas posibles de la ficha; sirve para sanear lo que llegue por la URL. */
const KNOWN_TABS = ['general', 'formatos', 'marca', 'cotizaciones', 'pagos', 'pasarelas', 'catalogos', 'crm', 'auditoria'] as const

/**
 * Ficha de la empresa, ahora con una pestaña por módulo. Todas las pestañas
 * comparten un único `useForm` y un guardado global: el `FormData` es plano y
 * `settings` es un jsonb anidado, así que los campos con prefijo se reconstruyen
 * en `toSettings`. Los cuerpos de las pestañas se ocultan con CSS (nunca se
 * desmontan) porque `useForm` lee el valor de cada campo del DOM al enviar.
 */
const EmpresaConfigPage: Component = () => {
  const params = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const toast = useToast()

  // `mutedColor` no se edita en esta pantalla: se conserva tal cual venía.
  const [savedSettings, setSavedSettings] = createSignal<CompanySettingsType>(DEFAULT_SETTINGS)

  const [company, { refetch }] = createResource(
    () => String(params.id),
    async (companyId: string) => {
      try {
        const data = await companyService.get(companyId)
        setSavedSettings(mergeSettings((data.settings ?? {}) as Partial<CompanySettingsType>))
        return data
      } catch (err: unknown) {
        toast.addToast({ title: resolveError(err), type: 'danger' })
        return null
      }
    }
  )

  // Miga de pan propia: "Empresas › [nombre]". Se limpia al salir de la ficha.
  createEffect(() => {
    const name = company()?.name
    if (name) setTrailOverride(['Empresas', name])
  })
  onCleanup(() => setTrailOverride(null))

  /** Cambiar de pestaña se refleja en la URL (?tab=...) para poder recargar y compartir la vista. */
  const sanitizeTab = (tab: string | undefined): string => (tab && (KNOWN_TABS as readonly string[]).includes(tab) ? tab : 'general')
  const [activeTab, setActiveTab] = createSignal(sanitizeTab(typeof searchParams.tab === 'string' ? searchParams.tab : undefined))

  const handleTabChange = (tab: string) => {
    setActiveTab(tab)
    setSearchParams({ ...searchParams, tab }, { replace: true })
  }

  const form = useForm({
    schema: settingsSchema,
    initialValues: () => {
      const s = savedSettings()
      return {
        name: company()?.name ?? '',
        slug: company()?.slug ?? '',
        domain: company()?.domain ?? '',

        currency: s.currency,
        dateFormat: s.locale.dateFormat,
        language: s.locale.language,
        notifyByEmail: s.notifyByEmail,
        notifyInApp: s.notifyInApp,
        notificationEmail: s.notificationEmail,

        brandingAppName: s.quoteBranding.appName,
        brandingPrimaryColor: s.quoteBranding.primaryColor,
        brandingSecondaryColor: s.quoteBranding.secondaryColor,
        brandingFooterText: s.quoteBranding.footerText,
        brandingLogoUrl: s.quoteBranding.logoUrl,

        issuerName: s.quoteIssuer.name,
        issuerDocument: s.quoteIssuer.document,
        issuerPhone: s.quoteIssuer.phone,
        issuerEmail: s.quoteIssuer.email,
        issuerAddress: s.quoteIssuer.address,

        defaultsCurrency: s.quoteDefaults.currency,
        defaultsTaxRate: String(s.quoteDefaults.taxRate),
        defaultsValidDays: String(s.quoteDefaults.validDays),
        defaultsTerms: s.quoteDefaults.terms,

        paymentBankName: s.paymentInfo.bankName,
        paymentAccountType: s.paymentInfo.accountType,
        paymentAccountNumber: s.paymentInfo.accountNumber,
        paymentAccountHolder: s.paymentInfo.accountHolder,
        paymentHolderDocument: s.paymentInfo.holderDocument,

        // Catálogos como arrays para los editores visuales
        currenciesJson: s.currencies,
        documentTypesJson: s.documentTypes,
        // Las etapas se editan con el editor visual, que trabaja con el array
        // ya parseado (el Textarea JSON de antes queda fuera).
        customerStatesJson: s.customerStates,
        bankAccountTypesJson: s.bankAccountTypes,
        addressLabelsJson: s.addressLabels,
        customerClassificationsJson: s.customerClassifications,

        numberLocale: s.locale.numberLocale,
        dateLocale: s.locale.dateLocale,
        country: s.locale.country
      }
    },
    onSubmit: async values => {
      const companyId = String(params.id)
      if (!companyId) {
        toast.addToast({ title: 'No hay una empresa seleccionada', type: 'danger' })
        return
      }

      await companyService.update(companyId, {
        name: values.name,
        slug: values.slug || values.name.toLowerCase().replace(/\s+/g, '-'),
        domain: values.domain || undefined,
        settings: toSettings(values) as unknown as Record<string, unknown>
      })
      toast.addToast({ title: 'Configuración de la empresa guardada', type: 'success' })
      await refetch()
    },
    onError: err => toast.addToast({ title: resolveError(err), type: 'danger' })
  })

  /** Parseo seguro de un catálogo del formulario: el `Form` ya entrega el array
   *  parseado, o el string (JSON o vacío) si no. Cae al valor guardado ante errores. */
  const parseJson = <T,>(json: unknown, fallback: T): T => {
    if (Array.isArray(json)) return json as T
    if (typeof json !== 'string') return fallback
    try {
      const parsed = JSON.parse(json) as T
      return Array.isArray(parsed) ? parsed : fallback
    } catch {
      return fallback
    }
  }

  /** Vuelve a armar el árbol de `settings` a partir de los campos planos. */
  const toSettings = (values: SettingsFormType) => {
    const prev = savedSettings()
    return {
      currency: values.currency,
      notifyByEmail: values.notifyByEmail,
      notifyInApp: values.notifyInApp,
      notificationEmail: values.notificationEmail,
      locale: {
        ...prev.locale,
        country: values.country,
        language: values.language,
        dateFormat: values.dateFormat,
        numberLocale: values.numberLocale,
        dateLocale: values.dateLocale
      },
      currencies: parseJson(values.currenciesJson, prev.currencies),
      documentTypes: parseJson(values.documentTypesJson, prev.documentTypes),
      customerStates: parseJson(values.customerStatesJson, prev.customerStates),
      bankAccountTypes: parseJson(values.bankAccountTypesJson, prev.bankAccountTypes),
      addressLabels: parseJson(values.addressLabelsJson, prev.addressLabels),
      customerClassifications: parseJson(values.customerClassificationsJson, prev.customerClassifications),
      quoteBranding: {
        appName: values.brandingAppName,
        primaryColor: values.brandingPrimaryColor,
        secondaryColor: values.brandingSecondaryColor,
        // No se edita aquí: se respeta lo que ya estaba guardado.
        mutedColor: prev.quoteBranding.mutedColor,
        footerText: values.brandingFooterText,
        logoUrl: values.brandingLogoUrl
      },
      quoteIssuer: {
        name: values.issuerName,
        document: values.issuerDocument,
        phone: values.issuerPhone,
        email: values.issuerEmail,
        address: values.issuerAddress
      },
      quoteDefaults: {
        currency: values.defaultsCurrency,
        taxRate: values.defaultsTaxRate,
        validDays: values.defaultsValidDays,
        terms: values.defaultsTerms
      },
      paymentInfo: {
        bankName: values.paymentBankName,
        accountType: values.paymentAccountType,
        accountNumber: values.paymentAccountNumber,
        accountHolder: values.paymentAccountHolder,
        holderDocument: values.paymentHolderDocument
      }
    }
  }

  const logoUrl = () => (form.values().brandingLogoUrl as string) ?? ''

  /** Opciones de moneda desde `settings.currencies` (configurable, no hardcodeado). */
  const currencyOptions = () => savedSettings().currencies.map(c => ({ value: c.code, title: `${c.name} (${c.code})` }))

  /**
   * El logo se guarda incrustado en `settings` como data URI: no hay
   * almacenamiento de ficheros y así viaja con la configuración de la empresa.
   */
  const handleLogoFile = (file?: File) => {
    if (!file) return
    if (file.size > 1_048_576) {
      toast.addToast({ title: 'El logo no puede superar 1 MB', type: 'warning' })
      return
    }
    const reader = new FileReader()
    reader.onload = () => form.setValue('brandingLogoUrl', String(reader.result ?? ''))
    reader.onerror = () => toast.addToast({ title: 'No pudimos leer la imagen', type: 'danger' })
    reader.readAsDataURL(file)
  }

  const tabs: TabItemType[] = [
    { id: 'general', label: 'General', icon: 'material-symbols-light:domain' },
    { id: 'formatos', label: 'Formatos', icon: 'material-symbols-light:tune' },
    { id: 'marca', label: 'Marca', icon: 'material-symbols-light:palette-outline' },
    { id: 'cotizaciones', label: 'Cotizaciones y PDF', icon: 'material-symbols-light:description-outline' },
    { id: 'pagos', label: 'Pagos', icon: 'material-symbols-light:account-balance-outline' },
    { id: 'pasarelas', label: 'Pasarelas', icon: 'material-symbols-light:credit-card-outline' },
    { id: 'catalogos', label: 'Catálogos', icon: 'material-symbols-light:format-list-bulleted' },
    { id: 'crm', label: 'Estados del cliente', icon: 'material-symbols-light:view-kanban-outline' },
    { id: 'auditoria', label: 'Auditoría', icon: 'material-symbols-light:database-outline' }
  ]

  const tabBody = (tab: string) => (activeTab() === tab ? 'pt-5' : 'hidden')

  return (
    <Container>
      <Title>Configuración de empresa | Florez Jaramillo</Title>

      <GoBack label="Volver a empresas" fallbackPath="/admin/empresas" />

      <h1 class="text-2xl font-bold text-foreground mt-3">{company()?.name ?? 'Empresa'}</h1>
      <p class="text-sm text-foreground-subtle mb-4">Datos y preferencias de la empresa: formatos, PDF de cotizaciones, datos de pago y catálogos.</p>

      <Show when={company.error}>
        <Card>
          <Card.Body>
            <div class="text-center py-8 text-danger">No se pudo cargar la configuración: {resolveError(company.error)}</div>
          </Card.Body>
        </Card>
      </Show>

      <Show when={company() && !company.error}>
        <Form id="company-settings-form" controller={form}>
          <Tabs items={tabs} activeTab={activeTab()} onChange={handleTabChange} variant="underline" />

          <div class={tabBody('general')}>
            <TabGeneral form={form} />
          </div>

          <div class={tabBody('formatos')}>
            <TabFormatos form={form} currencyOptions={currencyOptions} />
          </div>

          <div class={tabBody('marca')}>
            <TabMarca form={form} />
          </div>

          <div class={tabBody('cotizaciones')}>
            <TabCotizaciones form={form} logoUrl={logoUrl} handleLogoFile={handleLogoFile} currencyOptions={currencyOptions} />
          </div>

          <div class={tabBody('pagos')}>
            <TabPagos form={form} />
          </div>

          <div class={tabBody('pasarelas')}>
            <TabPasarelas companyId={params.id ?? ''} />
          </div>

          <div class={tabBody('catalogos')}>
            <TabCatalogos form={form} />
          </div>

          <div class={tabBody('crm')}>
            <TabCrm form={form} />
          </div>

          <div class={tabBody('auditoria')}>
            <TabAuditoria companyId={String(params.id)} />
          </div>

          <div class="flex justify-end mt-6 space-x-4">
            <Button type="button" color="default" variant="soft" onClick={() => refetch()} disabled={form.submitting() || !form.dirty()}>
              Descartar cambios
            </Button>
            <Button type="submit" color="primary" disabled={form.submitting() || company.loading || !form.valid()}>
              {form.submitting() ? 'Guardando...' : 'Guardar Empresa'}
            </Button>
          </div>
        </Form>
      </Show>
    </Container>
  )
}

export default EmpresaConfigPage
